import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { USER_SCREENS } from '../../../../store/ui/ui.constants'
import { deleteUserFavorite } from '../../../../store/userAccount/userAccount.actions'
import {
  selectUserFavorites,
  selectUserFavoritesLimitNotReached
} from '../../../../store/userAccount/userAccount.selectors'
import RectangleActionButton, {
  COLOR_GREEN_EMPTY,
  SIZE_MEDIUM
} from '../../../genericComponents/buttons/RectangleActionButton'
import { useT } from '../../../i18n/T'
import { useAction } from '../../../utils/react-redux'
import UserScreenContent from '../../shared/UserScreenContent'
import UserScreenHeader from '../../shared/UserScreenHeader'
import { FAVORITE_TYPES, HOME_ICON, WORK_ICON } from '../../UserAccount.constants'
import UserFavoriteItem from './UserFavoriteItem'

import { TAG_CLICK_USER_FAVORITES_LIST } from '../../../analytics/analytics.tagids.constants'
import { setUiUserScreen } from '../../../../store/ui/uiSlice'
import analyticsConstants from '../../../analytics/analytics.constants'
import { ACTION, TYPE } from '../../../analytics/ClickTagsService'
import { useTemporaryVisible } from '../../../genericComponents/useTemporaryVisible'
import UserFavoriteError from './UserFavoriteError'
import s from './UserManageFavorite.less'

const buildTagData = (type, action) => ({
  type: TAG_CLICK_USER_FAVORITES_LIST,
  tagInfo: { [ACTION]: action, [TYPE]: type }
})

const UserFavorites = () => {
  const dispatch = useDispatch()
  const { home, work, others } = useSelector(selectUserFavorites) || {}
  const execDeleteUserFavorite = useAction(deleteUserFavorite)
  const [visible, setVisible] = useTemporaryVisible()
  const [favoriteError, setFavoriteError] = useState()

  const manageFavorite = type => dispatch(setUiUserScreen({ name: USER_SCREENS.manageFavorite, params: { type } }))
  const deleteFavorite = useCallback(async uuid => {
    const deleteFavoriteResult = await execDeleteUserFavorite({ uuid })

    if (deleteFavoriteResult?.error) {
      setVisible(true)
      setFavoriteError(deleteFavoriteResult.error)
    }
  }, [])

  const canStillAddFavorite = useSelector(selectUserFavoritesLimitNotReached)

  const renderAddBtn = useCallback(() => {
    if (canStillAddFavorite) {
      return (
        <RectangleActionButton
          label="Ajouter"
          color={COLOR_GREEN_EMPTY}
          size={SIZE_MEDIUM}
          onClick={() => manageFavorite(FAVORITE_TYPES.OTHER)}
          tagData={buildTagData(
            analyticsConstants.user.favorites.type.other,
            analyticsConstants.user.favorites.action.listAdd
          )}
        />
      )
    }
  }, [manageFavorite])

  return (
    <>
      {visible && <div className={s.errorMessage}>{favoriteError}</div>}
      <UserScreenHeader rightChildren={renderAddBtn()}>{'Mes lieux favoris'}</UserScreenHeader>
      <UserFavoriteError marginTop={-10} />
      <UserScreenContent>
        <UserFavoriteItem
          key={home?.uuid}
          address={useT('useraccount.favorite.type-your-address')} // that prop will be overrided by `...home` if favorite is set
          favIcon={HOME_ICON} // that prop will be overrided but it’s usefull when no favorite exist
          {...home}
          action={() => manageFavorite(FAVORITE_TYPES.HOME)}
          label={useT('useraccount.favorite.home')}
          actionIcon={'crayon-v6'}
          allowEmpty
          editTagData={buildTagData(
            analyticsConstants.user.favorites.type.home,
            analyticsConstants.user.favorites.action.listEdit
          )}
          gotoTagData={buildTagData(
            analyticsConstants.user.favorites.type.home,
            analyticsConstants.user.favorites.action.listView
          )}
        />
        <UserFavoriteItem
          key={work?.uuid}
          address={useT('useraccount.favorite.type-your-address')} // that prop will be overrided by `...work` if favorite is set
          favIcon={WORK_ICON} // that prop will be overrided but it’s usefull when no favorite exist
          {...work}
          action={() => manageFavorite(FAVORITE_TYPES.WORK)}
          label={useT('useraccount.favorite.work')}
          actionIcon={'crayon-v6'}
          allowEmpty
          editTagData={buildTagData(
            analyticsConstants.user.favorites.type.work,
            analyticsConstants.user.favorites.action.listEdit
          )}
          gotoTagData={buildTagData(
            analyticsConstants.user.favorites.type.work,
            analyticsConstants.user.favorites.action.listView
          )}
        />
        {(others || []).map((other, idx) => (
          <UserFavoriteItem
            key={other.uuid || idx}
            {...other}
            action={deleteFavorite}
            actionIcon={'poubelle'}
            editTagData={buildTagData(
              analyticsConstants.user.favorites.type.other,
              analyticsConstants.user.favorites.action.listDelete
            )}
            gotoTagData={buildTagData(
              analyticsConstants.user.favorites.type.other,
              analyticsConstants.user.favorites.action.listView
            )}
          />
        ))}
      </UserScreenContent>
    </>
  )
}

export default UserFavorites
